<template>
  <div class="modern-tab-panel">
    <!-- Render each section based on its type -->
    <div
      v-for="(section, index) in sections"
      :key="index"
      class="panel-section"
    >
      <!-- Hero Section -->
      <div v-if="section.type === 'hero'" class="hero-section">
        <h2 class="hero-title">
          <i v-if="section.icon" :class="section.icon"></i>
          {{ section.title }}
        </h2>
        <p v-if="section.subtitle" class="hero-subtitle">{{ section.subtitle }}</p>
        <div class="hero-content" v-html="section.content"></div>
      </div>

      <!-- Nested Tabs -->
      <NestedTabs
        v-else-if="section.type === 'tabs'"
        :tabs="section.tabs"
        :defaultTab="section.defaultTab"
      />

      <!-- Accordion -->
      <AccordionSection
        v-else-if="section.type === 'accordion'"
        :items="section.items"
        :defaultExpanded="section.defaultExpanded"
        :multiExpand="section.multiExpand"
      />

      <!-- Card Grid -->
      <CardGrid
        v-else-if="section.type === 'cards'"
        :cards="section.cards"
        :columns="section.columns"
      />

      <!-- Simple HTML Content -->
      <div v-else-if="section.type === 'html'" class="html-section">
        <h3 v-if="section.title" class="section-title">
          <i v-if="section.icon" :class="section.icon"></i>
          {{ section.title }}
        </h3>
        <div v-html="section.content"></div>
      </div>

      <!-- Stats Section -->
      <div v-else-if="section.type === 'stats'" class="stats-section">
        <div class="stats-grid">
          <div v-for="(stat, idx) in section.stats" :key="idx" class="stat-item">
            <i v-if="stat.icon" :class="stat.icon"></i>
            <div class="stat-value">{{ stat.value }}</div>
            <div class="stat-label">{{ stat.label }}</div>
          </div>
        </div>
      </div>

      <!-- Timeline Section -->
      <div v-else-if="section.type === 'timeline'" class="timeline-section">
        <div class="timeline">
          <div v-for="(item, idx) in section.items" :key="idx" class="timeline-item">
            <div class="timeline-marker">
              <i v-if="item.icon" :class="item.icon"></i>
            </div>
            <div class="timeline-content">
              <h4>{{ item.title }}</h4>
              <p v-if="item.description">{{ item.description }}</p>
              <div v-if="item.content" v-html="item.content"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import NestedTabs from './NestedTabs.vue'
import AccordionSection from './AccordionSection.vue'
import CardGrid from './CardGrid.vue'

interface Section {
  type: 'hero' | 'tabs' | 'accordion' | 'cards' | 'html' | 'stats' | 'timeline'
  [key: string]: any
}

defineProps<{
  sections: Section[]
}>()
</script>

<style scoped>
.modern-tab-panel {
  /* Base styles already in parent */
}

.panel-section {
  margin-bottom: 40px;
}

.panel-section:last-child {
  margin-bottom: 0;
}

/* Hero Section */
.hero-section {
  text-align: center;
  padding: 40px 0;
  margin-bottom: 32px;
}

.hero-title {
  font-size: 32px;
  font-weight: 800;
  color: #1E293B;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.hero-title i {
  font-size: 32px;
  color: #3b82f6;
}

.hero-subtitle {
  font-size: 18px;
  color: #64748B;
  margin: 0 0 24px 0;
}

.hero-content {
  font-size: 16px;
  line-height: 1.8;
  color: #475569;
  max-width: 800px;
  margin: 0 auto;
}

/* HTML Section */
.html-section {
  padding: 24px 0;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: #1E293B;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-title i {
  font-size: 24px;
  color: #3b82f6;
}

/* Stats Section */
.stats-section {
  padding: 32px 0;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
}

.stat-item {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(168, 85, 247, 0.05));
  padding: 28px;
  border-radius: 16px;
  text-align: center;
  border: 1px solid rgba(139, 92, 246, 0.2);
  transition: all 0.3s ease;
}

.stat-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(139, 92, 246, 0.2);
}

.stat-item i {
  font-size: 32px;
  color: #3b82f6;
  margin-bottom: 12px;
}

.stat-value {
  font-size: 36px;
  font-weight: 800;
  color: #3b82f6;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  font-weight: 600;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Timeline Section */
.timeline-section {
  padding: 32px 0;
}

.timeline {
  position: relative;
  padding-left: 40px;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 15px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(180deg, #3b82f6, #2563eb, #1a1a1a);
}

.timeline-item {
  position: relative;
  padding-bottom: 32px;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-marker {
  position: absolute;
  left: -40px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
  z-index: 1;
}

.timeline-marker i {
  color: white;
  font-size: 14px;
}

.timeline-content {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(139, 92, 246, 0.1);
}

.timeline-content h4 {
  font-size: 18px;
  font-weight: 700;
  color: #1E293B;
  margin: 0 0 8px 0;
}

.timeline-content p {
  font-size: 14px;
  color: #64748B;
  margin: 0 0 12px 0;
  line-height: 1.6;
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 24px;
  }

  .hero-subtitle {
    font-size: 16px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .timeline {
    padding-left: 32px;
  }

  .timeline-marker {
    left: -32px;
    width: 28px;
    height: 28px;
  }
}
</style>
